// Avvisi push condivisi tra il quaderno di mamma (sw.js, scope «/») e l'admin
// (admin-sw.js, scope «/admin»). Nessun testo qui: ogni schermata parla con la sua voce.

export type PushHttp = { get<T>(url: string): Promise<T>; post<T>(url: string, body?: unknown): Promise<T> };
/** `stato` risponde almeno con `{ publicKey }` (la chiave VAPID pubblica). */
export type PushEndpoints = { stato: string; subscribe: string; unsubscribe: string };
export type StatoPush = 'attivo' | 'bloccato' | 'spento' | 'non_supportato' | 'errore';

/** Il browser sa fare push: service worker, PushManager e Notification. */
export function supportoPush(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Aperta dall'icona sulla schermata Home (su iOS le notifiche esistono solo così). */
export function appInstallata(): boolean {
  return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function chiaveVapid(base64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buf;
}

/** La registrazione dello scope richiesto. Si cerca per scope e non con `ready`: con il
 *  service worker di radice già attivo, `ready` su /admin può rispondere con quello sbagliato.
 *  Se il worker è appena registrato si aspetta che sia attivo (subscribe lo pretende). */
async function registrazione(scope: string): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.getRegistration(scope);
  if (!reg) return navigator.serviceWorker.ready;
  const worker = reg.active ? null : (reg.installing ?? reg.waiting);
  if (worker) {
    await new Promise<void>((resolve) => {
      const controlla = (): void => {
        if (worker.state !== 'activated' && worker.state !== 'redundant') return;
        worker.removeEventListener('statechange', controlla);
        resolve();
      };
      worker.addEventListener('statechange', controlla);
      controlla();
    });
  }
  return reg;
}

export async function statoPush(scope: string): Promise<StatoPush> {
  if (!supportoPush()) return 'non_supportato';
  try {
    const reg = await registrazione(scope);
    const sub = await reg.pushManager.getSubscription();
    const perm = Notification.permission;
    if (sub && perm === 'granted') return 'attivo';
    if (perm === 'denied') return 'bloccato';
    return 'spento';
  } catch {
    return 'errore';
  }
}

/** Da chiamare direttamente nel click: il permesso è il PRIMO await, perché l'attivazione
 *  utente di WebKit dura poco e dopo una richiesta di rete la finestra è già chiusa. */
export async function attivaPush(http: PushHttp, ep: PushEndpoints, scope: string): Promise<'attivo' | 'rifiutato'> {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return 'rifiutato';
  const { publicKey } = await http.get<{ publicKey: string }>(ep.stato);
  const reg = await registrazione(scope);
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: chiaveVapid(publicKey) });
  try {
    await http.post(ep.subscribe, { subscription: sub.toJSON(), userAgent: navigator.userAgent });
  } catch (err) {
    // Il server non l'ha registrata: non lasciare un'iscrizione «fantasma» nel browser,
    // altrimenti lo stato direbbe «attivo» senza che arrivi mai nulla.
    await sub.unsubscribe().catch(() => undefined);
    throw err;
  }
  return 'attivo';
}

/** Prima il server (così smette di inviare), poi il browser. Senza iscrizione non fa nulla. */
export async function spegniPush(http: PushHttp, ep: PushEndpoints, scope: string): Promise<void> {
  const reg = await registrazione(scope);
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await http.post(ep.unsubscribe, { endpoint: sub.endpoint });
  await sub.unsubscribe();
}
